/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import axios from "axios";

/**
 * Recarga todos los datos de una reserva tras un reset o modificación importante.
 * @param {Object} params - Parámetros y setters para repoblar estados del frontend.
 * @param {string} reservationId - ID de la reserva a recargar.
 * @param {function} setFormData
 * @param {function} setCustomerData
 * @param {function} setGuestCustomers
 * @param {function} setGuestChildren
 * @param {Object} formData - (opcional) Estado actual de formData si lo necesitas para merge.
 */
export async function refetchReservationAfterReset({
  reservationId,
  setFormData,
  setCustomerData,
  setGuestCustomers,
  setGuestChildren,
  formData
}) {
  console.log("🔁 Entrando en refetchReservationAfterReset");

  try {
    if (!reservationId) {
      console.warn("❌ No reservationId recibido en refetchReservationAfterReset");
      return;
    }
        
        const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservationId}`,
      {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("palantir_token")
        }
      }
    );
    const data = response.data;
    console.log("🔍 [PRUEBA CLAVE] DATA DEL BACKEND:", JSON.stringify(data, null, 2));

    const updatedFormData = {
      check_in_date: data.check_in_date || "",
      check_in_time: data.check_in_time || "",
      check_out_date: data.check_out_date || "",
      check_out_time: data.check_out_time || "",
      arrival_date: data.arrival_date || "",
      arrival_time: data.arrival_time || "",
      departure_date: data.departure_date || "",
      departure_time: data.departure_time || "",
      room_category_id: data.room_category_id || "",
      room_id: data.room_id || "",
      rate_id: data.rate_id || "",
      group_rate_id: data.group_rate_id || "",
      adults: data.adults || "",
      children: data.children || "",
      children_ages: Array.isArray(data.children_ages) ? data.children_ages.join(",") : "",
      babies: data.babies || 0,
      special_requests: data.special_requests || "",
      payment_status: data.payment_status || "Pending",
      total_price: data.total_price || "",
      night_price: data.night_price || "",
      has_override: !!data.has_override,
      created_by: data.created_by || "Admin",
      updated_by: data.updated_by || "",
      cancellation_reason: data.cancellation_reason || "",
      status: data.status || "Draft",
      business_unit_id: data.business_unit_id || 1,
    };


    console.log("⚠️ [Previo al overwrite] formData.check_in_date era:", formData?.check_in_date);
    setFormData((prev) => ({
      ...prev,
      ...updatedFormData,
      reservation_group_id: data.reservation_group_id || prev.reservation_group_id,
      group_locator: data.group?.group_locator || prev.group_locator,
      group_total_rooms: data.group?.number_of_rooms || prev.group_total_rooms,
      group_rate_id: data.group_rate_id || prev.group_rate_id,
      group_rate_name: data.group_rate_name || prev.group_rate_name,
    }));

    // Solo llama a los setters si existen
    if (setCustomerData) {
      const mainGuest = data.guests?.find(g => g.is_main_guest);
      if (mainGuest?.customer) {
        setCustomerData(mainGuest.customer);
      } else {
        setCustomerData(null);
      }
    }

    if (setGuestCustomers) {
      const accompanyingGuests = data.guests?.filter(g => !g.is_main_guest && !g.is_child) || [];
      const updatedGuests = [];
      accompanyingGuests.forEach(g => {
        if (typeof g.guest_index === "number") {
          updatedGuests[g.guest_index] = g.customer || g;
        }
      });
      setGuestCustomers(updatedGuests);
    }

    if (setGuestChildren) {
      const childGuests = data.guests?.filter(g => g.is_child) || [];
      const updatedChildren = [];
      childGuests.forEach(g => {
        if (typeof g.guest_index === "number") {
          updatedChildren[g.guest_index] = g.customer || g;
        }
      });
      setGuestChildren(updatedChildren);
    }

    console.log("🔁 Recarga tras reset completada");

  } catch (error) {
    console.error("❌ Error al recargar reserva tras reset:", error);
  }
};
